import { CourseMaterial } from "./coursesLoader.ts";
import useProgramDecision from "./useProgramDecision.tsx";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";
import styles from "./MaterialPrice.module.scss";
import { FormattedNumber } from "react-intl";
import { forwardRef, ReactNode } from "react";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import { findMaterialInCart } from "./common/storeProductUtils.ts";
import useVirtualStore from "./useVirtualStore.tsx";

const PriceWrapper = forwardRef<HTMLDivElement, { children?: ReactNode }>(
  function PriceWrapper({ children }, ref) {
    return (
      <div
        ref={ref}
        className={styles.selectedPrice}
        data-testid="selected-price"
      >
        {children}
      </div>
    );
  }
);

function isStringPrice(
  props: Parameters<typeof PriceValue>[0]
): props is { studentPrice: string } {
  return !("currency" in props);
}

function PriceValue(
  props:
    | {
        studentPrice: number | null;
        currency: string | null;
      }
    | { studentPrice: string }
) {
  if (isStringPrice(props)) {
    return <PriceWrapper>{props.studentPrice}</PriceWrapper>;
  } else {
    const price = Number(props.studentPrice);
    if (price <= 0) {
      return null;
    }
    return (
      <PriceWrapper>
        <FormattedNumber
          value={price}
          style="currency"
          currency={props.currency ?? "USD"}
        />
      </PriceWrapper>
    );
  }
}

function MaterialPrice(
  {
    material,
  }: {
    material: CourseMaterial;
  },
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { isVirtualStore } = useVirtualStore();
  const decision = useProgramDecision();
  const { optWindowOpen } = useMaterialOptDeadline(material);
  const layoutData = useLayoutData();
  const cartItem = findMaterialInCart(
    material,
    layoutData.storeCart?.lineItems
  );

  if (!isVirtualStore) {
    return null;
  }

  if (
    decision === "inclusive_access" &&
    optWindowOpen &&
    material.materialStatus === "opted_in"
  ) {
    return (
      <>
        <PriceValue
          studentPrice={material.studentPrice}
          currency={material.currency}
        />
      </>
    );
  } else if (cartItem) {
    return <PriceValue studentPrice={cartItem.price.currency} />;
  } else {
    // rendered by StudentChoice portal when appropriate
    return <PriceWrapper ref={ref} />;
  }
}

const Price = forwardRef(MaterialPrice);

export default Price;
